import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Users, Filter } from "lucide-react";
import { Facebook, Instagram, Linkedin, Twitter } from "lucide-react";
import axios from "axios";

//const API_BASE_URL = "http://localhost:8129/api-family";
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const DEFAULT_AVATAR = "/images/default-avatar.jpg";

// Helper
const formatDate = (dateStr) =>
  dateStr
    ? new Date(dateStr).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

// Calculate age group
const getAgeGroup = (birthDate) => {
  if (!birthDate) return "Unknown";
  const age = new Date().getFullYear() - new Date(birthDate).getFullYear();
  if (age < 20) return "Under 20";
  if (age < 40) return "20s–30s";
  if (age < 60) return "40s–50s";
  return "60+";
};

export default function FamilyDirectory() {
  const { id } = useParams();
  const [members, setMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeFilter, setActiveFilter] = useState("All");

  const itemsPerPage = 15;

useEffect(() => {
  const fetchMembers = async () => {
    try {
      setLoading(true);

      // Replace with your actual API endpoint
      const response = await axios.get(`${API_BASE_URL}/family/${id}/members`);

      if (response.data) {
        setMembers(response.data);
      } else {
        setMembers([]);
      }
    } catch (error) {
      console.error("Error fetching family members:", error);
      setMembers([]);
    } finally {
      setLoading(false);
    }
  };

  fetchMembers();
}, [id]);


  // Apply filters
  const filteredMembers = members.filter((m) => {
    const term = searchTerm.toLowerCase();
    const name = `${m.firstName} ${m.lastName}`.toLowerCase();
    const matchSearch = name.includes(term) || m.birthDate?.includes(term);

    const ageGroup = getAgeGroup(m.birthDate);
    const matchFilter = activeFilter === "All" || ageGroup === activeFilter;

    return matchSearch && matchFilter;
  });

  const totalPages = Math.ceil(filteredMembers.length / itemsPerPage);
  const start = (currentPage - 1) * itemsPerPage;
  const currentMembers = filteredMembers.slice(start, start + itemsPerPage);

  // Filter buttons
  const filters = ["All", "Under 20", "20s–30s", "40s–50s", "60+"];

  return (
    <div className="bg-background px-8 py-2">
      <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-center mb-8">
          <div className="flex items-center gap-3 mb-4 sm:mb-0">
            <div className="p-3 bg-purple-100 rounded-2xl">
              <Users className="text-purple-700 w-6 h-6" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800">Family Directory</h1>
          </div>

          {/* Search */}
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by name or birth date"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:outline-none"
            />
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap justify-center gap-3 mb-8">
          {filters.map((filter) => (
            <motion.button
              key={filter}
              whileTap={{ scale: 0.95 }}
              onClick={() => setActiveFilter(filter)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 border
                ${
                  activeFilter === filter
                    ? "bg-purple-600 text-white shadow-lg border-purple-700"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-purple-100"
                }`}
            >
              {filter}
            </motion.button>
          ))}
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => setActiveFilter("All")}
            className="px-5 py-2 rounded-full text-sm bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-300"
          >
            Clear
          </motion.button>
        </div>

        {/* Members Grid */}
        {loading ? (
          <p className="text-center text-gray-500 py-10">Loading members...</p>
        ) : currentMembers.length === 0 ? (
          <p className="text-center text-gray-500 py-10">No members found.</p>
        ) : (
          <>
            <div className="grid gap-6 grid-cols-[repeat(auto-fill,minmax(200px,1fr))]">
              {currentMembers.map((m) => (
                <motion.div
                  key={m.id}
                  whileHover={{ y: -5, scale: 1.03 }}
                  className="bg-gradient-to-br from-purple-50 to-white rounded-2xl border border-gray-100 shadow-md overflow-hidden cursor-pointer transition-all"
                >
                  <div className="relative">
                    <img
                      src={m.avatar || DEFAULT_AVATAR}
                      alt={`${m.firstName || ""} ${m.lastName || ""}`}
                      className="w-full h-56 object-cover"
                      onError={(e) => (e.target.src = DEFAULT_AVATAR)}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent rounded-2xl"></div>
                  </div>
                  <div className="p-4 text-center">
                    <h3 className="font-semibold text-lg text-gray-800">
                      {m.firstName} {m.lastName}
                    </h3>
                    {m.birthDate && (
                      <p className="text-gray-500 text-sm">
                        Born {formatDate(m.birthDate)}
                      </p>
                    )}



                    {/* Social media icons */}

                      <div className="flex justify-center gap-4 mt-3">
                        {m.facebook && (
                          <a
                            href={m.facebook}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 transition-transform transform hover:scale-110"
                            title="Facebook"
                          >
                            <Facebook className="w-5 h-5" />
                          </a>
                        )}
                        {m.instagram && (
                          <a
                            href={m.instagram}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-pink-600 hover:text-pink-800 transition-transform transform hover:scale-110"
                            title="Instagram"
                          >
                            <Instagram className="w-5 h-5" />
                          </a>
                        )}
                        {m.twitter && (
                          <a
                            href={m.twitter}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-700 hover:text-blue-900 transition-transform transform hover:scale-110"
                            title="twitter"
                          >
                            <Twitter className="w-5 h-5" />
                          </a>
                        )}
                      </div>

                  </div>

                </motion.div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center mt-8 gap-2 flex-wrap">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-1.5 rounded-full border bg-white hover:bg-purple-100 disabled:opacity-50 transition"
                >
                  Prev
                </button>

                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentPage(i + 1)}
                    className={`px-3 py-1.5 rounded-full transition ${
                      currentPage === i + 1
                        ? "bg-purple-600 text-white shadow"
                        : "bg-white border hover:bg-purple-100"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-1.5 rounded-full border bg-white hover:bg-purple-100 disabled:opacity-50 transition"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
